import { HyperionStepState } from 'app/hyperion/exercise-generation/model/hyperion-generation-stages';
import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { HyperionStageComponent } from 'app/hyperion/shared/stage/hyperion-stage.component';

import { ArtemisTranslatePipe } from 'app/foundation/pipes/artemis-translate.pipe';
import { HyperionActivityView, formatElapsed } from 'app/hyperion/exercise-generation/model/hyperion-generation-activity';
import { HyperionStage, HyperionStageKey, HyperionSubstepKey, stageLabelKey } from 'app/hyperion/exercise-generation/model/hyperion-generation-stages';
import { HyperionRunActivityComponent } from 'app/hyperion/exercise-generation/run/hyperion-run-activity.component';
import { serverTimeSignal } from 'app/hyperion/exercise-generation/hyperion-server-time.util';
import { ExerciseGenerationRepairRound } from 'app/openapi/model/exercise-generation-repair-round';

interface ProgressSubstep {
    key: HyperionSubstepKey;
    state: HyperionStepState;
    labelKey: string;
}

/** One rung of the ladder with every binding resolved, so no template binding calls a method. */
interface ProgressStep {
    key: HyperionStageKey;
    state: HyperionStepState;
    labelKey: string;
    substeps?: ProgressSubstep[];
    summaryKey?: string;
    summaryParams?: { turns: number; files: number };
    /** The one step that carries the live region and the activity panel. */
    detail: boolean;
    /** Whether that detail needs a rule to separate it from a substep ladder printed directly above it. */
    detailRuled: boolean;
}

/** How long a stage has been going, or how long it took. Kept apart from {@link ProgressStep} so the ladder is not rebuilt every second. */
interface StageTiming {
    key: string;
    duration: string;
    /** True while the stage is the one running, which is what makes the duration a clock rather than a record. */
    live: boolean;
}

/** Generation stages, substeps, and elapsed times on the dedicated run page. */
@Component({
    selector: 'jhi-hyperion-run-progress',
    templateUrl: './hyperion-run-progress.component.html',
    styleUrl: './hyperion-run-progress.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ArtemisTranslatePipe, HyperionRunActivityComponent, HyperionStageComponent],
})
export class HyperionRunProgressComponent {
    readonly stages = input.required<readonly HyperionStage[]>();
    /** An adaptation labels the authoring rung as revising the exercise rather than designing one. */
    readonly adapting = input(false);
    /** The newest message from the server, shown under the stage it belongs to. */
    readonly liveMessage = input<string | undefined>();
    readonly repairRound = input<ExerciseGenerationRepairRound | undefined>();
    /** What the agent is doing, shown under the same stage as the live message. */
    readonly activity = input<HyperionActivityView | undefined>();
    /** `compact` drops the recent-activity list so the ladder fits the code editor's bottom panel. */
    readonly density = input<'full' | 'compact'>('full');
    /** Forwarded to the activity area, which promotes Cancel into the stage row once the run stalls. */
    readonly cancelAvailable = input(false);
    readonly cancelPending = input(false);
    /** Forwarded so the stall wording only promises the run is "still connected" while that is true. */
    readonly connected = input(true);

    readonly cancelRequested = output<void>();

    protected readonly compact = computed(() => this.density() === 'compact');

    private readonly now = serverTimeSignal();

    /** The repair round only makes sense while the review stage is the one running. */
    protected readonly visibleRepairRound = computed(() => {
        const stage = this.stages().find((candidate) => candidate.key === 'review');
        return stage?.state === 'current' ? this.repairRound() : undefined;
    });

    /**
     * The stage the detail belongs under: the one that is running, or - once the run is over - the one it stopped in,
     * so the last thing the agent said does not vanish the moment the run ends.
     */
    protected readonly detailStageKey = computed<HyperionStageKey | undefined>(() => {
        const stages = this.stages();
        return (stages.find((stage) => stage.state === 'current') ?? stages.findLast((stage) => stage.state === 'failed') ?? stages.findLast((stage) => stage.state === 'complete'))
            ?.key;
    });

    protected readonly steps = computed<ProgressStep[]>(() => {
        const detailKey = this.detailStageKey();
        return this.stages().map((stage) => {
            const substeps =
                stage.substeps && stage.state !== 'pending'
                    ? stage.substeps.map((substep) => ({ key: substep.key, state: substep.state, labelKey: `artemisApp.hyperion.generation.substep.${substep.key}` }))
                    : undefined;
            return {
                key: stage.key,
                state: stage.state,
                labelKey: stageLabelKey(stage.key, this.adapting()),
                // A stage nobody has reached yet must not preview the work it might do.
                substeps,
                // Files are omitted when the stage wrote none, for the same reason the meter omits a counter at zero.
                summaryKey: stage.summary
                    ? stage.summary.files > 0
                        ? 'artemisApp.hyperion.generation.stageSummary'
                        : 'artemisApp.hyperion.generation.stageSummaryTurns'
                    : undefined,
                summaryParams: stage.summary,
                detail: stage.key === detailKey,
                detailRuled: stage.key === detailKey && substeps !== undefined,
            };
        });
    });

    /**
     * Every stage's duration, keyed by stage.
     *
     * Separate from {@link steps} so a ticking second only re-renders these few strings, and the clock is only read at
     * all while some stage is actually running - once the run ends this stops depending on the ticker entirely.
     */
    protected readonly timings = computed<Partial<Record<HyperionStageKey, StageTiming>>>(() => {
        const stages = this.stages();
        const running = stages.some((stage) => stage.runningSince !== undefined);
        const now = running ? this.now() : 0;
        const timings: Partial<Record<HyperionStageKey, StageTiming>> = {};
        for (const stage of stages) {
            const since = stage.runningSince === undefined ? Number.NaN : Date.parse(stage.runningSince);
            const live = Number.isFinite(since);
            // A clock skew must never subtract from a stage's recorded time, so the live part is clamped at zero.
            const totalMs = stage.elapsedMs + (live ? Math.max(0, now - since) : 0);
            if (totalMs <= 0 && !live) {
                continue;
            }
            timings[stage.key] = {
                key: live ? 'artemisApp.hyperion.generation.stage.runningFor' : 'artemisApp.hyperion.generation.stage.took',
                duration: formatElapsed(Math.floor(totalMs / 1000)),
                live,
            };
        }
        return timings;
    });
}
